import React, { useEffect, useState } from "react";
import '../../../styles/Figures.css'
import { betTransaction, getBetsBydate } from "../../../services/player/BetService.tsx";
import { getVimplayTransactionsForPlayer } from "../../../services/admin/BetsService.tsx";
import { useUser } from "../../../context/UserContext.tsx";
import { BetDetails, ParlaySelection } from "../../../models/MyBetModel.tsx";
import { Transaction } from "../../../models/User.tsx";
import { groupCasinoPlays, type CasinoPlay } from "../../admin/weekly-user/CasinoTransactionDetails.tsx";
import { VimplayTransaction } from "../../../models/VimplayTransaction.tsx";
import { formatCurrency, formatNumber, formatNumbers, formatSelectedMlbPitchers, getBetFiguresDate, getBetMatchupLabel, getBetPrimaryName, getBetResultDelta, getPendingMarketSubLabel, isFreePlayBet } from "../../../helpers/functions.tsx";
import {
    addAdminFiguresCalendarDays,
    ADMIN_TIMEZONE_LABEL,
    formatAdminFiguresDateLabel as formatFiguresDateLabel,
    formatAdminFiguresDateTime as formatFiguresDateTime,
    getAdminFiguresBusinessDateKey as getFiguresBusinessDateKey,
    getAdminFiguresDateKey as getFiguresDateKey,
    getAdminFiguresZonedParts as getFiguresZonedParts,
} from "../../../helpers/adminFiguresTime.tsx";
import { getAdminWeekRangeWeeksAgo as getWeekRangeWeeksAgo } from "../../../helpers/adminWeeklyWeekUtils.tsx";
import { IoChevronDown, IoChevronForward, IoClose } from "react-icons/io5";
import BetLogo, { DEFAULT_PROPS_LOGO } from "../modals/BetLogo.tsx";
import ImageWithPlaceholder from "../../ImageWithPlaceholder.tsx";

const ADJUSTMENT_TYPES = new Set(["CreditAdjustment", "DebitAdjustment"]);

function getTransactionFiguresDate(tx: Transaction): Date | null {
    const createDate = tx.createDate as { seconds?: number; toDate?: () => Date } | Date | undefined;
    if (!createDate) return null;
    if (createDate instanceof Date) {
        return Number.isNaN(createDate.getTime()) ? null : createDate;
    }
    if (typeof createDate.toDate === "function") {
        const date = createDate.toDate();
        return Number.isNaN(date.getTime()) ? null : date;
    }
    if (typeof createDate.seconds === "number") {
        return new Date(createDate.seconds * 1000);
    }
    return null;
}

function isCreditDebitAdjustment(tx: Transaction): boolean {
    return ADJUSTMENT_TYPES.has(tx.transactionType);
}

function getCasinoFiguresDate(tx: VimplayTransaction): Date | null {
    return getTransactionFiguresDate({ createDate: tx.createdAt } as Transaction);
}

function casinoNet(rows: VimplayTransaction[]): number {
    return groupCasinoPlays(rows).reduce((sum, play) => sum + (play.refunded ? 0 : play.net), 0);
}

type ActivityPick = BetDetails | ParlaySelection;

function activityAmount(value: number): string {
    const abs = `$${formatCurrency(Math.abs(value), 0)}`;
    if (value > 0) return `+${abs}`;
    if (value < 0) return `-${abs}`;
    return abs;
}

function activityTone(value: number): string {
    if (value > 0) return " is-win";
    if (value < 0) return " is-loss";
    return "";
}

function activityResultMark(result?: string | null): { letter: string; tone: string } | null {
    if (result === "Won") return { letter: "W", tone: "is-win" };
    if (result === "Lost") return { letter: "L", tone: "is-loss" };
    if (result === "Push" || result === "Tie") return { letter: "P", tone: "" };
    return null;
}

function activityOddsLine(bet: ActivityPick): string {
    const market = String(bet.marketName ?? "").toLowerCase();
    const selectionType = String(bet.selectionType ?? "").trim();
    const isMoneyline = market.includes("moneyline") || selectionType.toUpperCase() === "ML";
    const price = bet.odd?.price != null
        ? formatNumber(bet.odd.price, bet.odd.selection_line ?? undefined, "price")
        : "";

    if (isMoneyline) {
        const prefix = selectionType && selectionType.toUpperCase() !== "ML" ? selectionType : "ML";
        return [prefix, price].filter(Boolean).join(" ");
    }

    const points = bet.odd?.points != null
        ? formatNumber(bet.odd.points, bet.odd.selection_line ?? undefined, "points")
        : "";
    return [points, price].filter(Boolean).join(" ");
}

function activityGroupTitle(player: BetDetails): string {
    const description = player.description?.trim();
    if (description) return description;
    const count = player.selections?.length ?? 0;
    const noun = player.betType === "Teaser" ? "Teaser" : "Parlay";
    return count > 0 ? `${noun} - ${count} Teams` : noun;
}

function activityScore(grader?: ActivityPick["grader"]): string {
    if (!grader?.away_team_display && !grader?.home_team_display) return "";
    const away = [grader.away_team_display, grader.away_score].filter((part) => part != null && part !== "").join(" ");
    const home = [grader.home_team_display, grader.home_score].filter((part) => part != null && part !== "").join(" ");
    return [away, home].filter(Boolean).join(" / ");
}

function casinoPlayLine(play: CasinoPlay): string {
    if (play.refunded) return "Refunded";
    const parts: string[] = [];
    if (play.bet > 0) parts.push(`Bet $${formatNumbers(play.bet)}`);
    if (play.win > 0) parts.push(`Won $${formatNumbers(play.win)}`);
    return parts.join(" · ");
}

function isArrowLogo(src?: string | null): boolean {
    return /\/(up|down)-arrow\.png(?:$|\?)/i.test(src ?? "");
}

function ActivityAvatar({ bet }: { bet?: ActivityPick | null }) {
    const src = bet?.logo?.trim();
    const arrow = isArrowLogo(src);
    return (
        <span className="activity-bet__avatar">
            {src ? (
                <ImageWithPlaceholder
                    src={src}
                    className={arrow ? "activity-bet__photo activity-bet__photo--mark" : "activity-bet__photo"}
                    placeholderClassName="activity-bet__photo-ph"
                    fallbackSrc={DEFAULT_PROPS_LOGO}
                    alt=""
                />
            ) : (
                <BetLogo bet={bet} className="activity-bet__photo activity-bet__photo--mark" alt="" />
            )}
        </span>
    );
}

function ActivityFacts({ rows }: { rows: { label: string; value?: React.ReactNode }[] }) {
    const visible = rows.filter((row) => row.value != null && row.value !== "");
    if (visible.length === 0) return null;
    return (
        <dl className="activity-facts">
            {visible.map((row) => (
                <div key={row.label} className="activity-facts__row">
                    <dt>{row.label}</dt>
                    <dd>{row.value}</dd>
                </div>
            ))}
        </dl>
    );
}

function getAdjustmentDelta(tx: Transaction): number {
    if (tx.transactionType === "CreditAdjustment") return tx.amount ?? 0;
    if (tx.transactionType === "DebitAdjustment") return -(tx.amount ?? 0);
    return 0;
}

function Figures() {

    const weeks = [
        { label: "This Week", value: 0 },
        { label: "Last Week", value: 1 },
        ...Array.from({ length: 11 }, (_, i) => ({ label: `${i + 2} Week's ago`, value: i + 2 })),
    ];
    const { user } = useUser();
    if (!user?.userName) {
        throw new Error("User not authenticated");
    }
    const initialRange = getWeekRangeWeeksAgo(0);
    const [selectedWeek, setSelectedWeek] = useState(0);
    const [startDate, setStartDate] = useState(initialRange.start);
    const [endDate, setEndDate] = useState(initialRange.end);
    const [userBet, setUserBet] = useState<(BetDetails)[]>([]);
    const [userTransactions, setUserTransactions] = useState<Transaction[]>([]);
    const [userCasino, setUserCasino] = useState<VimplayTransaction[]>([]);
    const [details, setDetails] = useState<(BetDetails)[]>([]);
    const [detailsTransactions, setDetailsTransactions] = useState<Transaction[]>([]);
    const [detailsCasino, setDetailsCasino] = useState<VimplayTransaction[]>([]);
    const [detailsDate, setDetailsDate] = useState<Date | null>(null);
    const [expandedPlayers, setExpandedPlayers] = useState<Set<string>>(new Set());
    const [detailsTitle, setDetailsTitle] = useState<string>('');
    const [detailsDates, setDetailsDates] = useState<string>('');

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const weeksAgo = Number(e.target.value);
        const range = getWeekRangeWeeksAgo(weeksAgo);
        setSelectedWeek(weeksAgo);
        setStartDate(range.start);
        setEndDate(range.end);
    };

    useEffect(() => {

        async function fetchBets() {
            if (!user?.userName) return;
            const [bets, transactions, casino] = await Promise.all([
                getBetsBydate(user.userName, startDate, endDate),
                betTransaction(user.userName, startDate, endDate),
                user.id
                    ? getVimplayTransactionsForPlayer({ playerId: user.id }, startDate, endDate)
                    : Promise.resolve([]),
            ]);
            setUserBet(bets);
            setUserTransactions(transactions.filter(isCreditDebitAdjustment));
            setUserCasino(casino);
        }

        fetchBets();
    }, [user?.userName, user?.id, startDate, endDate]);

    const dailyTotals: Record<string, number> = {};
    const dailyBetCounts: Record<string, number> = {};

    userBet.forEach((bet) => {
        const rawDate = getBetFiguresDate(bet);
        if (!rawDate) return;
        const key = getFiguresBusinessDateKey(rawDate);

        if (!dailyTotals[key]) {
            dailyTotals[key] = 0;
        }

        dailyTotals[key] += getBetResultDelta(bet);
        dailyBetCounts[key] = (dailyBetCounts[key] || 0) + 1;
    });

    userTransactions.forEach((tx) => {
        const rawDate = getTransactionFiguresDate(tx);
        if (!rawDate) return;
        const key = getFiguresBusinessDateKey(rawDate);

        if (!dailyTotals[key]) {
            dailyTotals[key] = 0;
        }

        dailyTotals[key] += getAdjustmentDelta(tx);
    });

    const casinoByDay: Record<string, VimplayTransaction[]> = {};
    userCasino.forEach((tx) => {
        const rawDate = getCasinoFiguresDate(tx);
        if (!rawDate) return;
        const key = getFiguresBusinessDateKey(rawDate);
        if (!casinoByDay[key]) casinoByDay[key] = [];
        casinoByDay[key].push(tx);
    });
    Object.entries(casinoByDay).forEach(([key, rows]) => {
        const plays = groupCasinoPlays(rows);
        if (plays.length === 0) return;
        dailyTotals[key] = (dailyTotals[key] || 0) + plays.reduce((sum, play) => sum + (play.refunded ? 0 : play.net), 0);
        if (!dailyBetCounts[key]) dailyBetCounts[key] = 1;
    });

    const totalWeek = Object.values(dailyTotals).reduce((sum, val) => sum + val, 0);
    const weekHasBets = Object.values(dailyBetCounts).some((count) => count > 0);

    const amountTone = (value: number, hasBets: boolean) => {
        if (value > 0) return " is-positive";
        if (value < 0) return " is-negative";
        if (hasBets) return " is-even";
        return "";
    };

    const handleDayClick = (date: Date) => {
        const selectedKey = getFiguresDateKey(date);
        const parts = getFiguresZonedParts(date);

        const filteredBets = userBet.filter(bet => {
            const betDate = getBetFiguresDate(bet);
            if (!betDate) return false;
            return getFiguresBusinessDateKey(betDate) === selectedKey;
        });

        const filteredTransactions = userTransactions.filter(tx => {
            const txDate = getTransactionFiguresDate(tx);
            if (!txDate) return false;
            return getFiguresBusinessDateKey(txDate) === selectedKey;
        });

        const filteredCasino = userCasino.filter(tx => {
            const txDate = getCasinoFiguresDate(tx);
            if (!txDate) return false;
            return getFiguresBusinessDateKey(txDate) === selectedKey;
        });

        setDetails(filteredBets);
        setDetailsTransactions(filteredTransactions);
        setDetailsCasino(filteredCasino);
        setExpandedPlayers(new Set());
        setDetailsDate(date);
        setDetailsTitle(`Activity for`);
        setDetailsDates(`${formatFiguresDateLabel(date, { weekday: 'long' })} ${parts.month}/${parts.day}`);

    };

    const handleWeekClick = (start: Date, end: Date) => {
        const startParts = getFiguresZonedParts(start);
        setDetailsDate(start);
        setDetailsTitle(`Week for`);
        setDetailsDates(`Week of ${startParts.month}/${startParts.day}`);


        const filteredBets = userBet.filter(bet => {
            const betDate = getBetFiguresDate(bet);
            if (!betDate) return false;
            return betDate >= start && betDate <= end;
        });

        const filteredTransactions = userTransactions.filter(tx => {
            const txDate = getTransactionFiguresDate(tx);
            if (!txDate) return false;
            return txDate >= start && txDate <= end;
        });

        const filteredCasino = userCasino.filter(tx => {
            const txDate = getCasinoFiguresDate(tx);
            if (!txDate) return false;
            return txDate >= start && txDate <= end;
        });

        setDetails(filteredBets);
        setDetailsTransactions(filteredTransactions);
        setDetailsCasino(filteredCasino);
        setExpandedPlayers(new Set());
    };

    const togglePlayerDetails = (key: string) => {
        setExpandedPlayers(prev => {
            const updated = new Set(prev);
            if (updated.has(key)) {
                updated.delete(key);
            } else {
                updated.add(key);
            }
            return updated;
        });
    };

    function isParlayOrTeaser(player: BetDetails) {
        return player.betType === 'Parlay' || player.betType === 'Teaser';
    }

    const formatDateTime = (timestamp: any) => {
        if (timestamp && timestamp.seconds !== undefined) {
            return `${formatFiguresDateTime(new Date(timestamp.seconds * 1000))} ${ADMIN_TIMEZONE_LABEL}`;
        } else {
            return;
        }
    };
    const casinoPlays = groupCasinoPlays(detailsCasino);
    const wagersCount = details.length + detailsTransactions.length + casinoPlays.length;

    const resultTotal = details.reduce((acc, bet) => acc + getBetResultDelta(bet), 0)
        + detailsTransactions.reduce((acc, tx) => acc + getAdjustmentDelta(tx), 0)
        + casinoNet(detailsCasino);

    const getResult = (player: any) => getBetResultDelta(player);



    return (
        <>
            <div className="figures-history">
                <label className="figures-history__week">
                    <select className="figures-history__select" value={selectedWeek} onChange={handleChange} aria-label="Select week">
                        {weeks.map(({ label, value }) => (
                            <option key={value} value={value}>
                                {label}
                            </option>
                        ))}
                    </select>
                    <IoChevronDown className="figures-history__week-icon" aria-hidden="true" />
                </label>
                <div className="figures-history__list">
                    <div className="figures-history__row figures-history__row--static">
                        <span className="figures-history__label">Carry</span>
                        <span className="figures-history__amount">
                            $0
                            <IoChevronForward className="figures-history__chevron" aria-hidden="true" />
                        </span>
                    </div>
                    {Array.from({ length: 7 }, (_, i) => {
                        const date = addAdminFiguresCalendarDays(startDate, i);
                        const parts = getFiguresZonedParts(date);
                        const dayShort = formatFiguresDateLabel(date, { weekday: 'short' });
                        const dayCapitalized = dayShort.charAt(0).toUpperCase() + dayShort.slice(1);
                        const label = `${dayCapitalized} ${parts.month}/${parts.day}`;
                        const key = getFiguresBusinessDateKey(date);
                        const valueNum = dailyTotals[key] || 0;
                        const hasBets = (dailyBetCounts[key] || 0) > 0;
                        const amount = `${valueNum < 0 ? "-" : ""}$${formatCurrency(Math.abs(valueNum), 0)}`;

                        return (
                            <div key={key} className="figures-history__row" onClick={() => handleDayClick(date)} data-bs-toggle="modal" data-bs-target="#betDetails">
                                <span className="figures-history__label">{label}</span>
                                <span className={`figures-history__amount${amountTone(valueNum, hasBets)}`}>
                                    {amount}
                                    <IoChevronForward className="figures-history__chevron" aria-hidden="true" />
                                </span>
                            </div>
                        );
                    })}

                    <div className="figures-history__row" onClick={() => handleWeekClick(startDate, endDate)} data-bs-toggle="modal" data-bs-target="#betDetails">
                        <span className="figures-history__label">Week Total</span>
                        <span className={`figures-history__amount${amountTone(totalWeek, weekHasBets)}`}>
                            {`${totalWeek < 0 ? "-" : ""}$${formatCurrency(Math.abs(totalWeek), 0)}`}
                            <IoChevronForward className="figures-history__chevron" aria-hidden="true" />
                        </span>
                    </div>
                    {/* <div className="row-info">
                        <div className="label-title">Transactions</div>
                        <div className="value">$0.00</div>
                    </div>
                    <div className="row-info total">
                        <div className="label-title">End Balance</div>
                        <div className="value">$ 0.00</div>
                    </div> */}
                </div>
            </div>


            <div className="modal fade" id="betDetails" tabIndex={-1} aria-labelledby="activityModalTitle">
                <div className="modal-dialog activity-modal__dialog">
                    <div className="modal-content activity-modal">
                        <div className="activity-modal__header">
                            <h2 id="activityModalTitle" className="activity-modal__title">
                                {detailsTitle}{" "}
                                {detailsDate
                                    ? formatFiguresDateLabel(detailsDate, { month: "numeric", day: "numeric" })
                                    : ""}
                            </h2>
                            <button type="button" className="activity-modal__dismiss" data-bs-dismiss="modal" aria-label="Close">
                                <IoClose />
                            </button>
                        </div>
                        <div className="activity-modal__summary">
                            <span>Wagers: <strong>{wagersCount}</strong></span>
                            <span className="activity-modal__result-wrap">
                                Result:
                                <strong className={`activity-modal__result${activityTone(resultTotal)}`}>{activityAmount(resultTotal)}</strong>
                            </span>
                        </div>
                        <div className="activity-modal__day">
                            <span className="activity-modal__day-label">{detailsDates}</span>
                            <span className="activity-modal__day-amount">Amount</span>
                        </div>
                        <div className="activity-modal__list">
                            {wagersCount === 0 && (
                                <p className="activity-modal__empty">No wagers for this period.</p>
                            )}
                            {details.map((player, index) => {
                                const uniqueKey = `${player.ticket ?? player.betDate}-${index}`;
                                const isPT = isParlayOrTeaser(player);
                                const result = getResult(player);
                                const expanded = expandedPlayers.has(uniqueKey);
                                const mark = activityResultMark(player.grader?.result);
                                const subLabel = getPendingMarketSubLabel(player);

                                const shaded = index % 2 === 1;
                                const betClass = `activity-bet${isPT ? " activity-bet--group" : ""}${shaded ? " activity-bet--shade" : ""}`;

                                if (isPT) {
                                    return (
                                        <article key={uniqueKey} className={betClass}>
                                            <button type="button" className="activity-bet__row" onClick={() => togglePlayerDetails(uniqueKey)}>
                                                <span className="activity-bet__copy">
                                                    <span className="activity-bet__name">{activityGroupTitle(player)}</span>
                                                </span>
                                                <span className={`activity-bet__amount${activityTone(result)}`}>{activityAmount(result)}</span>
                                            </button>
                                            <div className="activity-bet__legs">
                                                {player.selections?.map((selection, legIndex) => {
                                                    const legMark = activityResultMark(selection.grader?.result);
                                                    const legSub = getPendingMarketSubLabel(selection);
                                                    return (
                                                        <button
                                                            key={`${uniqueKey}-leg-${legIndex}`}
                                                            type="button"
                                                            className="activity-bet__row activity-bet__row--leg"
                                                            onClick={() => togglePlayerDetails(uniqueKey)}
                                                        >
                                                            <span className="activity-bet__rail">
                                                                <ActivityAvatar bet={selection} />
                                                            </span>
                                                            <span className="activity-bet__copy">
                                                                <span className="activity-bet__name">{getBetPrimaryName(selection)}</span>
                                                                <span className="activity-bet__line">
                                                                    {activityOddsLine(selection)}
                                                                    {legMark && (
                                                                        <span className={`activity-bet__mark ${legMark.tone}`}>{legMark.letter}</span>
                                                                    )}
                                                                </span>
                                                                {legSub && <span className="activity-bet__sub">{legSub}</span>}
                                                            </span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            {expanded && (
                                                <div className="activity-bet__details">
                                                    <ActivityFacts
                                                        rows={[
                                                            { label: "Ticket#", value: player.ticket },
                                                            { label: "Risk/Win", value: `$${formatNumbers(player.bet)} / $${formatNumbers(player.reward)}` },
                                                            { label: `Accepted (${ADMIN_TIMEZONE_LABEL})`, value: formatDateTime(player.betDate) },
                                                            { label: `Latest Update (${ADMIN_TIMEZONE_LABEL})`, value: formatDateTime(player.lastUpdateBySystem) },
                                                        ]}
                                                    />
                                                    {player.selections?.map((selection, legIndex) => (
                                                        <div key={`${uniqueKey}-detail-${legIndex}`} className="activity-bet__leg-detail">
                                                            <div className="activity-bet__leg-heading">
                                                                <ActivityAvatar bet={selection} />
                                                                <span className="activity-bet__copy">
                                                                    <span className="activity-bet__name">{getBetPrimaryName(selection)}</span>
                                                                    <span className="activity-bet__line">{activityOddsLine(selection)}</span>
                                                                </span>
                                                            </div>
                                                            <ActivityFacts
                                                                rows={[
                                                                    { label: "Ticket#", value: selection.ticket },
                                                                    { label: "Status", value: selection.grader?.result },
                                                                    { label: "Score", value: activityScore(selection.grader) },
                                                                    { label: "Game", value: getBetMatchupLabel(selection) },
                                                                    { label: "Sport", value: selection.sport },
                                                                    { label: "Selection", value: selection.marketName },
                                                                    { label: "Pitchers", value: formatSelectedMlbPitchers(selection.pitchers) ?? undefined },
                                                                    { label: `Scheduled (${ADMIN_TIMEZONE_LABEL})`, value: formatDateTime(selection.gameDate) },
                                                                    { label: `Latest Update (${ADMIN_TIMEZONE_LABEL})`, value: formatDateTime(selection.lastUpdateBySystem ?? player.lastUpdateBySystem) },
                                                                ]}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </article>
                                    );
                                }

                                return (
                                    <article key={uniqueKey} className={betClass}>
                                        <button type="button" className="activity-bet__row" onClick={() => togglePlayerDetails(uniqueKey)}>
                                            <ActivityAvatar bet={player} />
                                            <span className="activity-bet__copy">
                                                <span className="activity-bet__name">
                                                    {getBetPrimaryName(player)}
                                                    {isFreePlayBet(player) && (
                                                        <span className="activity-bet__free">{player.freeBet || "FP"}</span>
                                                    )}
                                                </span>
                                                <span className="activity-bet__line">
                                                    {activityOddsLine(player)}
                                                    {mark && (
                                                        <span className={`activity-bet__mark ${mark.tone}`}>{mark.letter}</span>
                                                    )}
                                                </span>
                                                {subLabel && <span className="activity-bet__sub">{subLabel}</span>}
                                            </span>
                                            <span className={`activity-bet__amount${activityTone(result)}`}>{activityAmount(result)}</span>
                                        </button>
                                        {expanded && (
                                            <ActivityFacts
                                                rows={[
                                                    { label: "Ticket#", value: player.ticket },
                                                    { label: "Status", value: player.grader?.result ? `${player.grader.result} (${getBetMatchupLabel(player)})` : "" },
                                                    { label: "Score", value: activityScore(player.grader) },
                                                    { label: "Risk/Win", value: `$${formatNumbers(player.bet)} / $${formatNumbers(player.reward)}` },
                                                    { label: "Game", value: getBetMatchupLabel(player) },
                                                    { label: "Sport", value: player.sport },
                                                    { label: "Selection", value: getPendingMarketSubLabel(player) || player.marketName },
                                                    { label: "Pitchers", value: formatSelectedMlbPitchers(player.pitchers) ?? undefined },
                                                    { label: `Scheduled (${ADMIN_TIMEZONE_LABEL})`, value: formatDateTime(player.gameDate) },
                                                    { label: `Accepted (${ADMIN_TIMEZONE_LABEL})`, value: formatDateTime(player.betDate) },
                                                    { label: `Latest Update (${ADMIN_TIMEZONE_LABEL})`, value: formatDateTime(player.lastUpdateBySystem) },
                                                ]}
                                            />
                                        )}
                                    </article>
                                );
                            })}
                            {detailsTransactions.map((tx, index) => {
                                const isCredit = tx.transactionType === "CreditAdjustment";
                                const value = getAdjustmentDelta(tx);
                                const shaded = (details.length + index) % 2 === 1;
                                return (
                                    <article key={tx.id} className={`activity-bet${shaded ? " activity-bet--shade" : ""}`}>
                                        <div className="activity-bet__row activity-bet__row--static">
                                            <span className="activity-bet__badge">{isCredit ? "C" : "D"}</span>
                                            <span className="activity-bet__copy">
                                                <span className="activity-bet__name">{tx.description}</span>
                                                <span className="activity-bet__line">Adjustment</span>
                                            </span>
                                            <span className={`activity-bet__amount${activityTone(value)}`}>{activityAmount(value)}</span>
                                        </div>
                                    </article>
                                );
                            })}
                            {casinoPlays.map((play, index) => {
                                const value = play.refunded ? 0 : play.net;
                                const shaded = (details.length + detailsTransactions.length + index) % 2 === 1;
                                return (
                                    <article key={play.id} className={`activity-bet${shaded ? " activity-bet--shade" : ""}`}>
                                        <div className="activity-bet__row activity-bet__row--static">
                                            <span className="activity-bet__badge">C</span>
                                            <span className="activity-bet__copy">
                                                <span className="activity-bet__name">{play.game}</span>
                                                {casinoPlayLine(play) && (
                                                    <span className="activity-bet__line">{casinoPlayLine(play)}</span>
                                                )}
                                            </span>
                                            <span className={`activity-bet__amount${activityTone(value)}`}>{activityAmount(value)}</span>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                        <div className="activity-modal__footer">
                            <button type="button" className="activity-modal__close" data-bs-dismiss="modal">Close</button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}


export default Figures;
